import { useState, useCallback } from 'react';

/**
 * Hook för hantering av inline-redigering av labels och priser
 * Hanterar state och funktioner för temporär redigering
 */
const useInlineEditing = (updateEstimat) => {
  // Inline label-redigering per rad
  const [editingLabels, setEditingLabels] = useState({});
  const [tempLabelValues, setTempLabelValues] = useState({});
  
  // Inline pris-redigering per rad
  const [editingPrices, setEditingPrices] = useState({});
  const [tempPriceValues, setTempPriceValues] = useState({});

  /**
   * Label-redigering
   */
  const startEditLabel = useCallback((labelKey, currentLabel) => {
    setEditingLabels(prev => ({ ...prev, [labelKey]: true }));
    setTempLabelValues(prev => ({ ...prev, [labelKey]: currentLabel || '' }));
  }, []);

  const cancelEditLabel = useCallback((labelKey) => {
    setEditingLabels(prev => ({ ...prev, [labelKey]: false }));
    setTempLabelValues(prev => {
      const { [labelKey]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const changeTempLabel = useCallback((labelKey, value) => {
    setTempLabelValues(prev => ({ ...prev, [labelKey]: value }));
  }, []);

  const autoSaveLabel = useCallback((categoryKey, formItemKey, labelKey) => {
    const newVal = (tempLabelValues[labelKey] || '').trim();
    updateEstimat(categoryKey, formItemKey, 'label', newVal);
    setEditingLabels(prev => ({ ...prev, [labelKey]: false }));
    setTempLabelValues(prev => {
      const { [labelKey]: _removed, ...rest } = prev;
      return rest;
    });
  }, [tempLabelValues, updateEstimat]);

  /**
   * Pris-redigering
   */
  const startEditPrice = useCallback((priceKey, currentPrice) => {
    setEditingPrices(prev => ({ ...prev, [priceKey]: true }));
    const price = typeof currentPrice === 'object' ? (currentPrice?.totalt || 0) : (currentPrice ?? 0);
    setTempPriceValues(prev => ({ ...prev, [priceKey]: String(price) }));
  }, []);

  const changeTempPrice = useCallback((priceKey, value) => {
    setTempPriceValues(prev => ({ ...prev, [priceKey]: value }));
  }, []);

  const autoSavePrice = useCallback((categoryKey, formItemKey, priceKey) => {
    const raw = tempPriceValues[priceKey];
    let newVal = parseFloat(String(raw).replace(/\s/g, '').replace(',', '.'));
    if (!isFinite(newVal) || isNaN(newVal) || newVal < 0) newVal = 0;
    updateEstimat(categoryKey, formItemKey, 'price', newVal);
    setEditingPrices(prev => ({ ...prev, [priceKey]: false }));
    setTempPriceValues(prev => {
      const { [priceKey]: _removed, ...rest } = prev;
      return rest;
    });
  }, [tempPriceValues, updateEstimat]);

  return {
    // Label state
    editingLabels,
    tempLabelValues,
    setEditingLabels,
    setTempLabelValues,
    // Label actions
    startEditLabel,
    cancelEditLabel,
    changeTempLabel,
    autoSaveLabel,
    // Price state
    editingPrices,
    tempPriceValues,
    // Price actions
    startEditPrice,
    changeTempPrice,
    autoSavePrice,
  };
};

export default useInlineEditing;
